import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import PageHeader from '../../components/common/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { cn, formatDateTime } from '../../lib/utils';
import { 
  Users, Search, Phone, Mail, Home, FileCheck, AlertTriangle, 
  Calendar, Clock, ChevronRight, CheckCircle, XCircle, Filter,
  UserPlus, Archive, ArchiveRestore
} from 'lucide-react';
import { toast } from 'sonner';

const MyTeamPage = () => {
  const { api, user } = useAuth();
  const [workers, setWorkers] = useState([]);
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHouse, setSelectedHouse] = useState('all');
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [workerShifts, setWorkerShifts] = useState([]);
  const [detailsOpen, setDetailsOpen] = useState(false);
  
  // Add worker dialog state
  const [addWorkerOpen, setAddWorkerOpen] = useState(false);
  const [newWorker, setNewWorker] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    password: '',
    employment_type: 'casual',
    assigned_houses: []
  });
  const [addingWorker, setAddingWorker] = useState(false);
  
  // Archive dialog state
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [workerToArchive, setWorkerToArchive] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [workersRes, housesRes] = await Promise.all([
        api.get('/manager/team'),
        api.get('/houses')
      ]);
      setWorkers(workersRes.data);
      // Filter houses to only show manager's assigned houses
      const managerHouses = housesRes.data.filter(h => 
        user.assigned_houses?.includes(h.id)
      );
      setHouses(managerHouses.length > 0 ? managerHouses : housesRes.data);
    } catch (error) {
      console.error('Failed to fetch team data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkerDetails = async (worker) => {
    setSelectedWorker(worker);
    setDetailsOpen(true);
    try {
      // Fetch worker's recent shifts
      const shiftsRes = await api.get('/shifts', { 
        params: { worker_id: worker.id } 
      });
      setWorkerShifts(shiftsRes.data.slice(0, 20)); // Last 20 shifts
    } catch (error) {
      console.error('Failed to fetch worker details:', error);
    }
  };

  const getComplianceStatus = (worker) => {
    const docs = worker.compliance_documents || [];
    if (docs.length === 0) return { status: 'none', label: 'No Documents', color: 'bg-gray-500' };
    
    const now = new Date();
    const expiredDocs = docs.filter(d => d.expiry_date && new Date(d.expiry_date) < now);
    const expiringDocs = docs.filter(d => {
      if (!d.expiry_date) return false;
      const expiry = new Date(d.expiry_date);
      const thirtyDaysFromNow = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
      return expiry >= now && expiry <= thirtyDaysFromNow;
    });

    if (expiredDocs.length > 0) {
      return { status: 'expired', label: `${expiredDocs.length} Expired`, color: 'bg-red-500' };
    }
    if (expiringDocs.length > 0) {
      return { status: 'expiring', label: `${expiringDocs.length} Expiring Soon`, color: 'bg-amber-500' };
    }
    return { status: 'valid', label: 'All Valid', color: 'bg-green-500' };
  };

  // Add worker handler
  const handleAddWorker = async () => {
    if (!newWorker.first_name || !newWorker.last_name || !newWorker.email || !newWorker.password) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    setAddingWorker(true);
    try {
      await api.post('/manager/team/add', {
        ...newWorker,
        assigned_houses: newWorker.assigned_houses.length > 0 ? newWorker.assigned_houses : houses.map(h => h.id).slice(0, 1)
      });
      toast.success('Worker added successfully');
      setAddWorkerOpen(false);
      setNewWorker({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        password: '',
        employment_type: 'casual',
        assigned_houses: []
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add worker');
    } finally {
      setAddingWorker(false);
    }
  };

  // Archive worker handler
  const handleArchiveWorker = async () => {
    if (!workerToArchive) return;
    
    try {
      await api.put(`/manager/team/${workerToArchive.id}/archive`);
      toast.success(`${workerToArchive.first_name} ${workerToArchive.last_name} has been archived`);
      setArchiveDialogOpen(false);
      setWorkerToArchive(null);
      setDetailsOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive worker');
    }
  };

  // Unarchive worker handler
  const handleUnarchiveWorker = async (worker) => {
    try {
      await api.put(`/manager/team/${worker.id}/unarchive`);
      toast.success(`${worker.first_name} ${worker.last_name} has been unarchived`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unarchive worker');
    }
  };

  const filteredWorkers = workers.filter(worker => {
    const matchesSearch = 
      `${worker.first_name} ${worker.last_name}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
      worker.email?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesHouse = selectedHouse === 'all' || 
      worker.assigned_houses?.includes(selectedHouse);
    
    return matchesSearch && matchesHouse;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="my-team-page">
      <PageHeader 
        title="My Team"
        description={`${workers.length} team members assigned to your houses`}
      />

      {/* Actions & Filters */}
      <Card className="border-border/40">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="team-search"
              />
            </div>
            <Select value={selectedHouse} onValueChange={setSelectedHouse}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="house-filter">
                <Home className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Filter by house" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Houses</SelectItem>
                {houses.map(house => (
                  <SelectItem key={house.id} value={house.id}>{house.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => setAddWorkerOpen(true)} data-testid="add-worker-btn">
              <UserPlus className="h-4 w-4 mr-2" />
              Add Worker
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Team Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredWorkers.map(worker => {
          const compliance = getComplianceStatus(worker);
          const isArchived = worker.is_archived;
          return (
            <Card 
              key={worker.id} 
              className={cn(
                "border-border/40 hover:shadow-md transition-shadow cursor-pointer",
                isArchived && "opacity-60"
              )}
              onClick={() => fetchWorkerDetails(worker)}
              data-testid={`worker-card-${worker.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-lg font-semibold text-primary">
                        {worker.first_name?.[0]}{worker.last_name?.[0]}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        {worker.first_name} {worker.last_name}
                        {isArchived && <Badge variant="outline" className="text-xs">Archived</Badge>}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {worker.role === 'house_manager' ? 'House Manager' : worker.position_title || 'Support Worker'}
                      </p>
                    </div>
                  </div>
                  {!isArchived && (
                    <Badge className={cn("text-xs text-white", compliance.color)}>
                      {compliance.label}
                    </Badge>
                  )}
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Mail className="h-4 w-4" />
                    <span className="truncate">{worker.email}</span>
                  </div>
                  {worker.phone && (
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <Phone className="h-4 w-4" />
                      <span>{worker.phone}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-muted-foreground">
                    <Home className="h-4 w-4" />
                    <span>
                      {worker.assigned_houses?.length || 0} house(s) assigned
                    </span>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-border/40 flex items-center justify-between">
                  <span className="text-xs text-muted-foreground">
                    {worker.employment_type === 'full_time' ? 'Full Time' : 
                     worker.employment_type === 'part_time' ? 'Part Time' : 
                     worker.employment_type === 'casual' ? 'Casual' : 'Staff'}
                  </span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredWorkers.length === 0 && (
        <Card className="border-border/40">
          <CardContent className="p-8 text-center">
            <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No workers found matching your search</p>
          </CardContent>
        </Card>
      )}

      {/* Worker Details Dialog */}
      <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedWorker && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <span className="font-semibold text-primary">
                      {selectedWorker.first_name?.[0]}{selectedWorker.last_name?.[0]}
                    </span>
                  </div>
                  {selectedWorker.first_name} {selectedWorker.last_name}
                </DialogTitle>
              </DialogHeader>

              <Tabs defaultValue="info" className="mt-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Basic Info</TabsTrigger>
                  <TabsTrigger value="compliance">Compliance</TabsTrigger>
                  <TabsTrigger value="shifts">Shift History</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4 mt-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Email</label>
                      <p className="font-medium flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        {selectedWorker.email}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Phone</label>
                      <p className="font-medium flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        {selectedWorker.phone || 'Not provided'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Employment Type</label>
                      <p className="font-medium">
                        {selectedWorker.employment_type === 'full_time' ? 'Full Time' : 
                         selectedWorker.employment_type === 'part_time' ? 'Part Time' : 
                         selectedWorker.employment_type === 'casual' ? 'Casual' : 'Not specified'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Start Date</label>
                      <p className="font-medium">
                        {selectedWorker.start_date ? formatDateTime(selectedWorker.start_date).split(' ')[0] : 'Not specified'}
                      </p>
                    </div>
                  </div>
                  
                  <div className="space-y-1">
                    <label className="text-sm text-muted-foreground">Assigned Houses</label>
                    <div className="flex flex-wrap gap-2">
                      {selectedWorker.assigned_houses?.map(houseId => {
                        const house = houses.find(h => h.id === houseId);
                        return (
                          <Badge key={houseId} variant="outline">
                            <Home className="h-3 w-3 mr-1" />
                            {house?.name || houseId}
                          </Badge>
                        );
                      })}
                      {(!selectedWorker.assigned_houses || selectedWorker.assigned_houses.length === 0) && (
                        <span className="text-muted-foreground">No houses assigned</span>
                      )}
                    </div>
                  </div>

                  {selectedWorker.notes && (
                    <div className="space-y-1">
                      <label className="text-sm text-muted-foreground">Notes</label>
                      <p className="text-sm bg-muted/30 p-3 rounded-lg">{selectedWorker.notes}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="compliance" className="mt-4">
                  <div className="space-y-3">
                    {selectedWorker.compliance_documents?.length > 0 ? (
                      selectedWorker.compliance_documents.map((doc, idx) => {
                        const isExpired = doc.expiry_date && new Date(doc.expiry_date) < new Date();
                        const isExpiring = doc.expiry_date && !isExpired && 
                          new Date(doc.expiry_date) < new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
                        
                        return (
                          <div 
                            key={idx}
                            className={cn(
                              "p-3 rounded-lg border",
                              isExpired ? "border-red-500/50 bg-red-500/10" :
                              isExpiring ? "border-amber-500/50 bg-amber-500/10" :
                              "border-border/40"
                            )}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <FileCheck className={cn(
                                  "h-4 w-4",
                                  isExpired ? "text-red-500" :
                                  isExpiring ? "text-amber-500" :
                                  "text-green-500"
                                )} />
                                <span className="font-medium">{doc.document_type || doc.name}</span>
                              </div>
                              <Badge className={cn(
                                "text-xs",
                                isExpired ? "bg-red-500" :
                                isExpiring ? "bg-amber-500" :
                                "bg-green-500"
                              )}>
                                {isExpired ? 'Expired' : isExpiring ? 'Expiring Soon' : 'Valid'}
                              </Badge>
                            </div>
                            {doc.expiry_date && (
                              <p className="text-sm text-muted-foreground mt-1">
                                Expires: {new Date(doc.expiry_date).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                        );
                      })
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <AlertTriangle className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No compliance documents on file</p>
                      </div>
                    )}
                  </div>
                </TabsContent>

                <TabsContent value="shifts" className="mt-4">
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {workerShifts.length > 0 ? (
                      workerShifts.map(shift => (
                        <div 
                          key={shift.id}
                          className="p-3 rounded-lg border border-border/40 flex items-center justify-between"
                        >
                          <div>
                            <p className="font-medium">{shift.house_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {shift.start_time?.split('T')[0]} • {shift.shift_type}
                            </p>
                          </div>
                          <Badge variant="outline" className={cn(
                            shift.status === 'completed' ? 'border-green-500 text-green-500' :
                            shift.status === 'accepted' ? 'border-blue-500 text-blue-500' :
                            shift.status === 'in_progress' ? 'border-amber-500 text-amber-500' :
                            ''
                          )}>
                            {shift.status}
                          </Badge>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-8 text-muted-foreground">
                        <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
                        <p>No shift history available</p>
                      </div>
                    )}
                  </div>
                </TabsContent>
              </Tabs>
              
              {/* Archive button in details dialog */}
              {selectedWorker && selectedWorker.role !== 'house_manager' && (
                <div className="mt-4 pt-4 border-t">
                  {selectedWorker.is_archived ? (
                    <Button 
                      variant="outline" 
                      onClick={() => handleUnarchiveWorker(selectedWorker)}
                      className="w-full"
                    >
                      <ArchiveRestore className="h-4 w-4 mr-2" />
                      Unarchive Worker
                    </Button>
                  ) : (
                    <Button 
                      variant="destructive" 
                      onClick={() => {
                        setWorkerToArchive(selectedWorker);
                        setArchiveDialogOpen(true);
                      }}
                      className="w-full"
                    >
                      <Archive className="h-4 w-4 mr-2" />
                      Archive Worker
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Worker Dialog */}
      <Dialog open={addWorkerOpen} onOpenChange={setAddWorkerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Add New Worker
            </DialogTitle>
            <DialogDescription>Add a new worker to your team</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>First Name *</Label>
                <Input 
                  value={newWorker.first_name}
                  onChange={(e) => setNewWorker({...newWorker, first_name: e.target.value})}
                  placeholder="First name"
                />
              </div>
              <div className="space-y-2">
                <Label>Last Name *</Label>
                <Input 
                  value={newWorker.last_name}
                  onChange={(e) => setNewWorker({...newWorker, last_name: e.target.value})}
                  placeholder="Last name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input 
                type="email"
                value={newWorker.email}
                onChange={(e) => setNewWorker({...newWorker, email: e.target.value})}
                placeholder="email@example.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Phone</Label>
              <Input 
                value={newWorker.phone}
                onChange={(e) => setNewWorker({...newWorker, phone: e.target.value})}
                placeholder="Phone number"
              />
            </div>
            <div className="space-y-2">
              <Label>Temporary Password *</Label>
              <Input 
                type="password"
                value={newWorker.password}
                onChange={(e) => setNewWorker({...newWorker, password: e.target.value})}
                placeholder="Set a temporary password"
              />
            </div>
            <div className="space-y-2">
              <Label>Employment Type</Label>
              <Select value={newWorker.employment_type} onValueChange={(v) => setNewWorker({...newWorker, employment_type: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="casual">Casual</SelectItem>
                  <SelectItem value="part_time">Part Time</SelectItem>
                  <SelectItem value="full_time">Full Time</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddWorkerOpen(false)}>Cancel</Button>
            <Button onClick={handleAddWorker} disabled={addingWorker}>
              {addingWorker ? 'Adding...' : 'Add Worker'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Archive Confirmation Dialog */}
      <Dialog open={archiveDialogOpen} onOpenChange={setArchiveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Archive className="h-5 w-5" />
              Archive Worker
            </DialogTitle>
            <DialogDescription>
              Are you sure you want to archive {workerToArchive?.first_name} {workerToArchive?.last_name}?
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground">
              This will:
            </p>
            <ul className="text-sm text-muted-foreground list-disc list-inside mt-2 space-y-1">
              <li>Prevent them from logging in</li>
              <li>Remove them from roster options</li>
              <li>Keep their historical data (notes, incidents) accessible</li>
            </ul>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setArchiveDialogOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleArchiveWorker}>
              Archive Worker
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyTeamPage;
